import { useState } from 'react'
import { Avatar } from '@components/atoms/Avatar'
import { useTheme } from '@context/ThemeContext'
import type { AdminBrandingConfig } from '../../../types/admin-config'
import type { User } from '../../../types/user'
import styles from './HomeHeader.module.scss'

export interface HomeHeaderProps {
  branding: AdminBrandingConfig | null
  user: User
  onProfileClick: () => void
  /** True while the branding API call is in flight. */
  loading?: boolean
  className?: string
}

const DEFAULT_LOGO_HEIGHT_PX = 28

/**
 * Resolves the actual logo URL to render for a given theme. Mirrors
 * `process-runner`'s `Logo` component so the two apps consume the same
 * `TenantDetails` payload identically:
 *
 *   const { dark = logo, light = logo } = additionalLogos || { dark: '', light: '' }
 *   const logoToBeUsed = (isDark ? dark : light) || logo
 */
function resolveLogoUrl(
  branding: AdminBrandingConfig,
  isDark: boolean,
): string | undefined {
  const { logo, additionalLogos } = branding
  const { dark = logo, light = logo } = additionalLogos ?? {
    dark: '',
    light: '',
  }
  return (isDark ? dark : light) || logo || undefined
}

/**
 * Resolves the background colour painted behind the logo image. Same
 * shape + fallback chain as process-runner's `getTenantBrandColour`.
 * Returns a CSS-ready value (`#rrggbb` or `'transparent'`).
 */
function resolveLogoBackgroundColor(
  branding: AdminBrandingConfig,
  isDark: boolean,
): string {
  const { logoBackgroundColor, additionalLogoBackgroundColor } = branding
  const themeBg = isDark
    ? additionalLogoBackgroundColor?.dark
    : additionalLogoBackgroundColor?.light
  const hex = themeBg || logoBackgroundColor
  return hex && hex !== 'undefined' ? `#${hex}` : 'transparent'
}

export function HomeHeader({
  branding,
  user,
  onProfileClick,
  loading = false,
  className,
}: HomeHeaderProps) {
  const { resolvedTheme } = useTheme()

  // Resolve the effective header theme. `'system'` mirrors the resolved
  // app theme; `'light'` and `'dark'` pin the header regardless of the
  // page background.
  const requested = branding?.selectedHeaderTheme ?? 'system'
  const effectiveTheme: 'light' | 'dark' =
    requested === 'system' ? resolvedTheme : requested
  const isDark = effectiveTheme === 'dark'
  const logoUrl = branding ? resolveLogoUrl(branding, isDark) : undefined
  const logoBgColor = branding
    ? resolveLogoBackgroundColor(branding, isDark)
    : 'transparent'

  // Track image-load state so an in-place skeleton shows on first load
  // and on theme change (when the resolved URL flips). Reset whenever
  // the URL changes — same "store info from previous renders" pattern
  // used in Avatar.
  const [imgLoaded, setImgLoaded] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)
  const [lastLogoUrl, setLastLogoUrl] = useState(logoUrl)
  if (logoUrl !== lastLogoUrl) {
    setLastLogoUrl(logoUrl)
    setImgLoaded(false)
    setImgFailed(false)
  }

  const altText = branding?.logoAltText ?? ''
  const logoMaxHeight = branding?.logoMaxHeightPx ?? DEFAULT_LOGO_HEIGHT_PX

  // Show the shimmer placeholder while the branding API is in flight,
  // or while we have a URL but the image hasn't decoded yet.
  const showSkeleton =
    loading || (Boolean(logoUrl) && !imgLoaded && !imgFailed)
  const showImage = Boolean(logoUrl) && !imgFailed

  const rootClasses = [
    styles.root,
    isDark ? styles.dark : styles.light,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <header className={rootClasses}>
      <div className={styles.brand}>
        {showSkeleton && (
          <div
            className={styles.logoSkeleton}
            style={{ height: logoMaxHeight }}
            data-testid="main-logo-skeleton"
            aria-busy="true"
            aria-label="Loading logo"
          />
        )}
        {showImage && (
          <img
            data-testid="main-logo"
            src={logoUrl}
            alt={altText}
            className={styles.logoImage}
            style={{
              maxHeight: logoMaxHeight,
              backgroundColor: logoBgColor,
              display: imgLoaded ? 'block' : 'none',
            }}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
          />
        )}
      </div>

      <Avatar user={user} size="md" onClick={() => onProfileClick()} />
    </header>
  )
}
